// =============================================================================
// Stan kartoteki kadrowej w powłoce — czysta warstwa sekcji „Kadry".
//
// Powłoka czyta mapę tożsamości (`shop_floor_worker_external_ids`, migracja
// 711) WPROST przez RLS, bez trasy odczytu w nexus-core: tabela nie niesie
// danych osobowych (identyfikatory, akronim, hashe, kod błędu), a polityka
// SELECT przepuszcza aktywnego członka organizacji. Nazwiska dokładane są
// z `shop_floor_workers` — czyli z kartoteki, którą ten sam człowiek i tak
// widzi.
//
// Funkcje są tu czyste, bo pytania, na które odpowiadają, są rozstrzygalne bez
// sieci: ilu pracowników ma tożsamość, kto czeka na decyzję człowieka, jak
// dawno był ostatni udany przebieg.
// =============================================================================

/** Wiersz mapy tożsamości tak, jak oddaje go RLS. Bez PII — patrz nagłówek. */
export interface WierszKadr {
  worker_id: string;
  external_id: string | null;
  acronym: string;
  last_status: string | null;
  last_error: string | null;
  synced_at: string | null;
  /** Rozstrzyga remis, gdy jeden pracownik ma wiersze z kilku firm (713). */
  updated_at?: string | null;
}

export interface PodsumowanieKadr {
  /** Pracownicy aktywni w kartotece Universe. */
  wszyscy: number;
  /** Ilu z nich ma potwierdzoną tożsamość po stronie systemu kadrowego. */
  zTozsamoscia: number;
  /** Ile wierszy czeka na decyzję człowieka (patrz `wymagajaUwagi`). */
  doUzgodnienia: number;
  /** Najświeższy `synced_at` w mapie — ISO albo null, gdy nic nie poszło. */
  ostatniaSynchronizacja: string | null;
}

/**
 * Stany, które NIE miną same. `ERROR` jest wśród nich świadomie: synchronizator
 * ponowi go po godzinie, ale administrator ma prawo wiedzieć, że wysyłka nie
 * doszła — cisza do następnego przebiegu jest tym samym, co brak informacji.
 */
const STANY_DO_UWAGI = new Set(['CONFLICT', 'NOT_VALID', 'AMBIGUOUS', 'ERROR']);

export function wymagajaUwagi(wiersze: WierszKadr[]): WierszKadr[] {
  return wiersze.filter((w) => STANY_DO_UWAGI.has(w.last_status ?? ''));
}

/**
 * Wiersze dotyczące pracowników z AKTYWNEJ kartoteki.
 *
 * Mapa tożsamości zatrzymuje wiersz pracownika po dezaktywacji (tamta strona
 * ma go dalej, z `INACTIVE`), a licznik „z ilu" liczy tylko aktywnych. Bez
 * tego filtru sekcja potrafiła napisać „43 z 42" — liczba większa od całości
 * jest dla czytającego sygnałem, że narzędzie się myli, nawet gdy myli się
 * tylko w tym jednym miejscu.
 */
export function tylkoAktywni(wiersze: WierszKadr[], aktywni: Set<string>): WierszKadr[] {
  return wiersze.filter((w) => aktywni.has(w.worker_id));
}

/**
 * Jeden wiersz na pracownika — najświeższy. Po 713 pracownik może mieć wiersze
 * z kilku firm systemu zewnętrznego (najemca przestawił `firma`), a „ostatni
 * z listy" znaczyłoby „ten, którego baza akurat oddała na końcu".
 */
export function najnowszyNaPracownika(wiersze: WierszKadr[]): Map<string, WierszKadr> {
  const wynik = new Map<string, WierszKadr>();
  for (const w of wiersze) {
    const dotad = wynik.get(w.worker_id);
    if (!dotad || (w.updated_at ?? '') > (dotad.updated_at ?? '')) wynik.set(w.worker_id, w);
  }
  return wynik;
}

export function podsumujKadry(wiersze: WierszKadr[], liczbaPracownikow: number): PodsumowanieKadr {
  let ostatnia: string | null = null;
  for (const w of wiersze) {
    if (w.synced_at && (ostatnia === null || w.synced_at > ostatnia)) ostatnia = w.synced_at;
  }
  return {
    wszyscy: liczbaPracownikow,
    // Tożsamość znaczy „system kadrowy wie, o kim mowa" — sam wiersz w mapie
    // nie wystarcza, bo AMBIGUOUS i CONFLICT też mają swój wiersz.
    zTozsamoscia: wiersze.filter((w) => Boolean(w.external_id)).length,
    doUzgodnienia: wymagajaUwagi(wiersze).length,
    ostatniaSynchronizacja: ostatnia,
  };
}

/**
 * „12 min temu" / „3 h temu" / „wczoraj". Bez bibliotek i bez sekund: sekcja
 * odświeża się na klik, więc precyzja do minuty jest i tak udawana.
 * `null` znaczy „nigdy" i tak też się to mówi.
 */
export function ileTemu(iso: string | null, teraz: Date = new Date()): string {
  if (!iso) return 'nigdy';
  const kiedy = Date.parse(iso);
  if (!Number.isFinite(kiedy)) return 'nieznana data';
  const minuty = Math.floor((teraz.getTime() - kiedy) / 60_000);
  if (minuty < 0) return 'przed chwilą';
  if (minuty < 1) return 'przed chwilą';
  if (minuty < 60) return `${minuty} min temu`;
  const godziny = Math.floor(minuty / 60);
  if (godziny < 24) return `${godziny} h temu`;
  const dni = Math.floor(godziny / 24);
  return dni === 1 ? 'wczoraj' : `${dni} dni temu`;
}

/** Jedno zdanie stanu nad listą — to, co administrator czyta najpierw. */
export function zdanieOKadrach(p: PodsumowanieKadr, teraz: Date = new Date()): string {
  const czesci = [`Pracownicy w Saldeo: ${p.zTozsamoscia} z ${p.wszyscy}`];
  if (p.doUzgodnienia > 0) czesci.push(`${p.doUzgodnienia} do przejrzenia`);
  czesci.push(`ostatnia synchronizacja ${ileTemu(p.ostatniaSynchronizacja, teraz)}`);
  return czesci.join(' · ');
}

/**
 * Prefiks listy kandydatów w `last_error`. ŚWIADOME zdublowanie stałej
 * `PREFIKS_KANDYDATOW` z `services/nexus-core/src/kadry/kadryCore.ts` — powłoka
 * nie importuje kodu usługi (inny pakiet, inny build), a rozjazd łapie test
 * wartownik, który czyta OBA źródła. Ta sama zasada, co przy manifeście wtyczek.
 */
export const PREFIKS_KANDYDATOW_POWLOKI = 'AMBIGUOUS:';

/**
 * Kandydaci do wskazania przy `AMBIGUOUS` — same identyfikatory pracownika po
 * stronie systemu kadrowego. Nie ma tu imion i nie jest to przeoczenie: mapa
 * tożsamości z założenia nie przechowuje danych osobowych OSÓB SPOZA Universe,
 * a kandydat jest właśnie kimś takim. Administrator rozstrzyga, otwierając
 * kartotekę w Saldeo — i to jest uczciwsze niż nazwisko przepisane do naszej
 * bazy po to, żeby ułatwić jedno kliknięcie.
 */
export function kandydaciWiersza(w: WierszKadr): string[] {
  const tekst = w.last_error;
  if (typeof tekst !== 'string' || !tekst.startsWith(PREFIKS_KANDYDATOW_POWLOKI)) return [];
  return tekst
    .slice(PREFIKS_KANDYDATOW_POWLOKI.length)
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

/**
 * Komunikat błędu do pokazania pod wierszem. Lista kandydatów NIE jest błędem
 * — jest danymi sterującymi wyborem, więc wypisana wprost byłaby szumem
 * („AMBIGUOUS:1201,1440") tam, gdzie człowiek spodziewa się zdania.
 */
export function komunikatWiersza(w: WierszKadr): string | null {
  if (!w.last_error) return null;
  if (w.last_error.startsWith(PREFIKS_KANDYDATOW_POWLOKI)) return null;
  return w.last_error;
}
